/* eslint-disable @typescript-eslint/no-explicit-any */
import * as dcmjs from 'dcmjs';
import { cloneDeep } from 'lodash';
import { AnonymizationStorage } from './anonymization_storage';
import { DICOMUtils } from './dicom_utils';
import { AnonymizationAttribute } from './models/anonymization_attribute';
import { AnonymizationConfig } from './models/anonymization_config';
import { AnonymizationRule } from './models/anonymization_rule';
import {
    extendMetaAnonymizationRule,
    MetainformationAnonymizationRule,
} from './models/metainformation_anonymization_rule';
import * as config from './whitelist-config.json';

export enum ModelType {
    Segmentation,
    Dose,
    Data,
    DoseCalc,
    ImageProcessing,
    Registration,
    Translation,
}

export const getModelTypeString = (modelType: ModelType) => {
    switch (modelType) {
        case ModelType.Segmentation: {
            return 'contouring';
        }
        case ModelType.Dose: {
            return 'dose';
        }
        case ModelType.Data: {
            return 'data';
        }
        case ModelType.DoseCalc: {
            return 'dosecalc';
        }
        case ModelType.ImageProcessing: {
            return 'imageprocessing';
        }
        case ModelType.Registration: {
            return 'reg';
        }
        case ModelType.Translation: {
            return 'translation';
        }
        default: {
            throw new TypeError(`Unknown model type: ${modelType}`);
        }
    }
};

export class DICOMAnonymizerService {
    public UIDMapping: { [index: string]: string };
    public originalPrivateVRs: { [index: string]: object };
    private _config: AnonymizationConfig;
    private _taskIDOverrides: Map<string, [string, string]>;
    private _storage: AnonymizationStorage;

    public constructor() {
        this._config = config;
        this._taskIDOverrides = new Map<string, [string, string]>();
        this._storage = new AnonymizationStorage();
        this.UIDMapping = {};
        this.originalPrivateVRs = {};
    }

    public anonymizeFiles(
        fileBuffers: ArrayBuffer[],
        modelType: ModelType
    ): ArrayBuffer[] {
        const originalDatasets = this.parseBuffers(fileBuffers, true);
        const anonymizedDatasets = this.anonymizeDatasets(
            originalDatasets,
            modelType
        );

        if (!anonymizedDatasets) return [];

        return this.saveToBuffer(anonymizedDatasets, originalDatasets);
    }

    public reidentifyFiles(fileBuffers: ArrayBuffer[], modelType: ModelType) {
        const anonDatasets = this.parseBuffers(fileBuffers);
        const reidentifiedDatasets = this.reidentifyDatasets(
            anonDatasets,
            modelType
        );

        if (!reidentifiedDatasets) return [];

        return this.saveToBuffer(reidentifiedDatasets, anonDatasets);
    }

    public extractPrivateTags(modality: string): string[] {
        const conf = this.calculateEffectiveConfig(modality);

        if (conf && conf.keep_attributes) {
            return conf.keep_attributes
                .filter((tag: string) => tag.includes(','))
                .map((tag: string) => tag.replaceAll(',', ''));
        }

        return [];
    }

    public parseBuffers(
        fileBuffers: ArrayBuffer[],
        isAnonymization: boolean = false
    ): any[][] {
        return fileBuffers.map((file: ArrayBuffer) => {
            const original = dcmjs.data.DicomMessage.readFile(file);
            const keptPrivateTags = [];

            if (isAnonymization) {
                const dataset =
                    dcmjs.data.DicomMetaDictionary.naturalizeDataset(
                        original.dict
                    );
                keptPrivateTags.push(
                    ...this.extractPrivateTags(dataset.Modality)
                );
            }
            return [
                dcmjs.data.DicomMetaDictionary.naturalizeDataset(
                    this.cleanPrivateTags(original.dict, keptPrivateTags)
                ),
                dcmjs.data.DicomMetaDictionary.namifyDataset(
                    this.cleanPrivateTags(original.meta, keptPrivateTags)
                ),
            ];
        });
    }

    public cleanPrivateTags(dict: any, keptPrivateTags: string[]): any {
        const result: any = {};

        for (const tag of Object.keys(dict)) {
            const tagHex = parseInt(tag, 16);

            // Extract Group and Element from the tag
            const group = (tagHex >> 16) & 0xffff;
            const isEven = group % 2 === 0;

            // Ignore odd groups which are higher than 8
            if (!isEven && group > 8) {
                if (keptPrivateTags.includes(tag.toLowerCase())) {
                    // console.debug(`Found private tag: ${tag}, but it is configured to be kept`);
                    this.originalPrivateVRs[tag] = dict[tag];
                } else {
                    // console.debug(`Ignoring private tag ${tag}`);
                    continue;
                }
            }

            result[tag] = dict[tag];
        }

        return result;
    }

    public saveToBuffer(
        datasets: any[][],
        originalDatasets: any[][]
    ): ArrayBuffer[] {
        return datasets.map((dataset: any[][], i: number) => {
            const [data, meta]: any[] = dataset;
            const ds: any = { ...data, _meta: meta, _vrMap: {} };

            const privateTags = this.extractPrivateTags(data.Modality);

            // set value representations
            if (i < originalDatasets.length) {
                const originalDataset = originalDatasets[i][0];

                const pixelData = originalDataset['_vrMap']?.PixelData;
                const rescaleIntercept =
                    originalDataset['_vrMap']?.RescaleIntercept;

                if (pixelData) ds._vrMap.PixelData = pixelData;
                if (rescaleIntercept)
                    ds._vrMap.RescaleIntercept = rescaleIntercept;
            }

            const file = dcmjs.data.datasetToDict(ds);
            file.dict = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(ds);

            privateTags.forEach((tag: string) => {
                const tagLookup = tag.replaceAll(',', '').toUpperCase();
                if (tagLookup in data) {
                    const vr = this.originalPrivateVRs[tagLookup];

                    if (vr) {
                        file.dict[tagLookup] = {
                            ...vr,
                            Value: data[tagLookup],
                        };
                    } else {
                        file.dict[tagLookup] = {
                            vr: 'UN',
                            Value: data[tagLookup],
                        };
                    }
                }
            });

            return file.write();
        });
    }

    private processMeta(dataset: any) {
        const newDataset: { [id: string]: any } = {};

        for (const [key, value] of Object.entries(dataset)) {
            newDataset[key] = {
                vr: DICOMUtils.getValueRepresentation(key),
                Value: [value],
            };
        }

        return newDataset;
    }

    private anonymizeDatasets(
        datasets: any[][],
        modelTypeInput: ModelType
    ): any[][] {
        const modelType = getModelTypeString(modelTypeInput).toLowerCase();
        const processedSOPs: string[] = [];
        let repeats = 0;
        const anonymizedDatasets: [any, any][] = [];

        while (datasets.length > anonymizedDatasets.length) {
            if (repeats > 5) {
                console.error('Anonymization failed after 5 attempts!');
                break;
            }
            repeats++;

            const modalities = datasets.map((dataset) =>
                dataset[0].Modality.toLowerCase()
            );
            const effectiveConfigs: {
                [id: string]: MetainformationAnonymizationRule;
            } = {};

            const dependenciesConfigs = this._config.dependencies[modelType];
            for (const modality of modalities) {
                effectiveConfigs[modality] =
                    this.calculateEffectiveConfig(modality);
            }

            for (const [mainDataset, metainfoDataset] of datasets) {
                let originalKey: string | null = null;
                let datasetKey: string | null = null;
                const modality = mainDataset.Modality.toLowerCase();
                const sop = mainDataset.SOPInstanceUID.toLowerCase();

                if (processedSOPs.includes(sop)) continue;

                if (dependenciesConfigs) {
                    if (dependenciesConfigs[modality]) {
                        [originalKey, datasetKey] =
                            this._storage.initializeDatasetAnonymization(
                                mainDataset,
                                this._config.aggregation,
                                modelType
                            );
                        const uids = DICOMUtils.matchingAttributes(
                            mainDataset,
                            dependenciesConfigs[modality]
                        );
                        for (const uid of uids) {
                            this._taskIDOverrides.set(uid, [
                                originalKey,
                                datasetKey,
                            ]);
                        }
                    } else {
                        if (modality == 'ct') {
                            const ctUID = mainDataset.SOPInstanceUID;
                            if (this._taskIDOverrides.has(ctUID)) {
                                [originalKey, datasetKey] =
                                    this._taskIDOverrides.get(ctUID)!;
                                this._taskIDOverrides.delete(ctUID);
                            } else {
                                if (repeats < 5) continue;
                            }
                        }
                    }
                }

                if (!datasetKey) {
                    [originalKey, datasetKey] =
                        this._storage.initializeDatasetAnonymization(
                            mainDataset,
                            this._config.aggregation,
                            modelType
                        );
                }

                const effectiveConfig = effectiveConfigs[modality];
                const anonymizedDataset = this.anonymizeDataset(
                    mainDataset,
                    effectiveConfig,
                    originalKey!,
                    datasetKey,
                    false
                );
                const anonymizedMeta = this.anonymizeDataset(
                    metainfoDataset,
                    effectiveConfig.metainformation!,
                    originalKey!,
                    datasetKey,
                    true
                );

                processedSOPs.push(sop);
                anonymizedDatasets.push([anonymizedDataset, anonymizedMeta]);

                if (dependenciesConfigs) {
                    this._storage.copyAnonymization(
                        originalKey!,
                        datasetKey,
                        anonymizedDataset,
                        this._config.aggregation,
                        modelType
                    );
                }
            }
        }

        return anonymizedDatasets;
    }

    private reidentifyDatasets(
        datasets: any[][],
        modelTypeInput: ModelType
    ): any[][] {
        const modelType = getModelTypeString(modelTypeInput).toLowerCase();
        const reidentifiedDatasets: [any, any][] = [];

        const modalities = datasets.map((dataset) =>
            dataset[0].Modality.toLowerCase()
        );
        const effectiveConfigs: {
            [id: string]: MetainformationAnonymizationRule;
        } = {};

        for (const modality of modalities) {
            effectiveConfigs[modality] =
                this.calculateEffectiveConfig(modality);
        }

        for (const [mainDataset, metainfoDataset] of datasets) {
            let matchedType = 'general';

            if (modelType in this._config.aggregation) {
                matchedType = modelType;
            }

            const baseRule =
                this._config.aggregation[modelType] ||
                this._config.aggregation['general'];
            const rule = { ...baseRule };

            if (matchedType in this._config.result_aggregation) {
                const resultRule = this._config.result_aggregation[matchedType];

                Object.keys(resultRule).forEach((key) => {
                    rule[key] = resultRule[key];
                });
            }

            const patientID = mainDataset.PatientID;
            const aggAttribute = DICOMUtils.attributeByRule(mainDataset, rule);
            const datasetKey = `${patientID}-${aggAttribute}`;

            if (!this._storage.hasDataset(datasetKey)) {
                console.error(
                    `Dataset ${datasetKey} not found in the original UID mapping!`
                );
                continue;
            }

            const modality = mainDataset.Modality.toLowerCase();
            const effectiveConfig = effectiveConfigs[modality];
            const anonymizedDataset = this.reidentifyDataset(
                mainDataset,
                effectiveConfig,
                datasetKey,
                false
            );
            const anonymizedMeta = this.reidentifyDataset(
                metainfoDataset,
                effectiveConfig.metainformation!,
                datasetKey,
                true
            );
            reidentifiedDatasets.push([anonymizedDataset, anonymizedMeta]);
        }

        return reidentifiedDatasets;
    }

    private anonymizeDataset(
        originalDataset: any,
        effectiveConfig: AnonymizationRule,
        originalKey: string,
        datasetKey: string,
        isMeta: boolean
    ): any {
        const anonymizedDataset: any = {};
        if (isMeta) {
            anonymizedDataset['FileMetaInformationVersion'] = {
                '0': 0,
                '1': 1,
            };
        } else {
            anonymizedDataset['PatientIdentityRemoved'] = 'YES';
            anonymizedDataset['DeidentificationMethod'] =
                'Per DICOM PS 3.15 AnnexE. Details in 0012,0064';
            anonymizedDataset['DeidentificationMethodCodeSequence'] = {
                CodeValue: '001',
                CodingSchemeDesignator: 'DCM',
                CodeMeaning: 'Basic Application Level Confidentiality Profile',
            };
        }

        const fixedTags = effectiveConfig.fixed_attributes
            ? Object.entries(effectiveConfig.fixed_attributes)
            : [];
        const keepTags = effectiveConfig.keep_attributes;

        if (fixedTags) {
            for (const [tag, rule] of fixedTags) {
                anonymizedDataset[tag] = rule;

                if (tag in originalDataset) {
                    const originalValue = DICOMUtils.metaTagValue(
                        originalDataset[tag]
                    );
                    this._storage.saveOriginalMapping(
                        tag,
                        originalValue,
                        datasetKey
                    );
                }
            }
        }

        if (keepTags) {
            for (const tag of keepTags) {
                let tagLookup = tag;
                // Comma means a private tag
                if (tag.includes(',')) {
                    tagLookup = tag.replaceAll(',', '').toUpperCase();
                }
                if (tagLookup in originalDataset) {
                    anonymizedDataset[tagLookup] = DICOMUtils.metaTagValue(
                        originalDataset[tagLookup]
                    );
                }
            }
        }

        if (isMeta) {
            return this.processMeta(
                this.anonymizeDatasetRecursive(
                    originalDataset,
                    anonymizedDataset,
                    effectiveConfig.anon_attributes,
                    originalKey,
                    datasetKey
                )
            );
        } else {
            return this.anonymizeDatasetRecursive(
                originalDataset,
                anonymizedDataset,
                effectiveConfig.anon_attributes,
                originalKey,
                datasetKey
            );
        }
    }

    private anonymizeDatasetRecursive(
        originalDataset: any,
        dataset: any | null,
        rules: AnonymizationAttribute[] | undefined | null,
        originalKey: string,
        datasetKey: string
    ): any {
        if (!dataset) {
            dataset = cloneDeep(originalDataset);
        }
        if (!rules) return dataset;

        for (const rule of rules) {
            if (rule.rule) {
                const dicomTag = rule.name;

                if (!dicomTag) continue;
                if (!(dicomTag in originalDataset)) continue;
                const updated = originalDataset[dicomTag]
                    .filter((sequenceItem: any) => sequenceItem)
                    .map((sequenceItem: any) =>
                        this.anonymizeDatasetRecursive(
                            sequenceItem,
                            null,
                            rule.rule,
                            originalKey,
                            datasetKey
                        )
                    );
                dataset[dicomTag] = updated;
            }

            if (rule.attributes) {
                for (const attribute of rule.attributes) {
                    const dicomTag = attribute;
                    const originalValue = DICOMUtils.metaTagValue(
                        originalDataset[dicomTag]
                    );

                    if (
                        !rule.keep_attributes ||
                        !rule.keep_attributes.includes(attribute)
                    ) {
                        const anonValue = this._storage.anonymizeUID(
                            originalValue,
                            originalKey,
                            datasetKey
                        );
                        dataset[dicomTag] = anonValue;

                        if (
                            attribute === 'SeriesInstanceUID' ||
                            attribute === 'SOPInstanceUID'
                        ) {
                            this.UIDMapping[originalValue] = anonValue;
                        }

                        if (attribute === 'SOPInstance') {
                            this._storage.saveAnonymizedMapping(
                                originalValue,
                                anonValue,
                                datasetKey
                            );
                        }
                    } else {
                        dataset[dicomTag] = originalValue;
                    }
                }
            }
        }

        return dataset;
    }

    private reidentifyDataset(
        anonDataset: any,
        effectiveConfig: AnonymizationRule,
        datasetKey: string,
        isMeta: boolean
    ): any {
        const resultDataset = cloneDeep(anonDataset);
        if (isMeta) {
            resultDataset.FileMetaInformationVersion = { '0': 0, '1': 1 };
        } else {
            resultDataset.PatientIdentityRemoved = 'NO';
        }
        this.reidentifyDatasetRecursive(
            anonDataset,
            resultDataset,
            effectiveConfig.anon_attributes,
            datasetKey
        );

        const fixedTags = effectiveConfig.fixed_attributes;
        const keepTags = effectiveConfig.keep_attributes;

        if (fixedTags) {
            for (const [tag, rule] of Object.entries(fixedTags)) {
                if (
                    tag in anonDataset &&
                    this._storage.hasOriginalValue(tag, datasetKey)
                ) {
                    try {
                        const originalValue = DICOMUtils.metaTagValue(
                            this._storage.getOriginalValue(tag, datasetKey)
                        );
                        resultDataset[tag] = originalValue;
                    } catch (ex) {
                        console.error(
                            `Error during the reidentification for the fixed tags on tag ${tag}!`,
                            ex
                        );
                        throw ex;
                    }
                }
            }
        }

        if (keepTags) {
            for (const rule of keepTags) {
                try {
                    if (rule in anonDataset) {
                        resultDataset[rule] = DICOMUtils.metaTagValue(
                            anonDataset[rule]
                        );
                    }
                } catch (ex) {
                    console.error(
                        `Error during the reidentification for the keep tags on tag ${rule}!`,
                        ex
                    );
                    throw ex;
                }
            }
        }

        return resultDataset;
    }

    private reidentifyDatasetRecursive(
        anonDataset: any,
        dataset: any | null,
        rules: AnonymizationAttribute[] | undefined | null,
        datasetKey: string
    ): any {
        if (!dataset) {
            dataset = cloneDeep(anonDataset);
        }
        if (!rules) return dataset;

        for (const rule of rules) {
            if (rule.rule) {
                const dicomTag = rule.name;
                if (!dicomTag) continue;
                if (!(dicomTag in anonDataset)) continue;
                const updated = anonDataset[dicomTag]
                    .filter((sequenceItem: any) => sequenceItem)
                    .map((sequenceItem: any) =>
                        this.reidentifyDatasetRecursive(
                            sequenceItem,
                            null,
                            rule.rule,
                            datasetKey
                        )
                    );
                dataset[dicomTag] = updated;
            }

            if (rule.attributes) {
                for (const attribute of rule.attributes) {
                    try {
                        if (attribute in anonDataset) {
                            const anonValue = DICOMUtils.metaTagValue(
                                anonDataset[attribute]
                            );

                            if (
                                this._storage.hasOriginalValue(
                                    anonValue,
                                    datasetKey
                                )
                            ) {
                                const originalValue = DICOMUtils.metaTagValue(
                                    this._storage.getOriginalValue(
                                        anonValue,
                                        datasetKey
                                    )
                                );
                                dataset[attribute] = originalValue;
                            } else {
                                dataset[attribute] =
                                    DICOMUtils.metaTagValue(anonValue);
                            }
                        }
                    } catch (ex) {
                        console.error(ex, 'Error during the reidentification');
                        throw ex;
                    }
                }
            }
        }

        return dataset;
    }

    private calculateEffectiveConfig(
        modality: string
    ): MetainformationAnonymizationRule {
        let effectiveConfig = {
            ...this._config.generic,
        } as MetainformationAnonymizationRule;
        const configOverride =
            this._config.modality_overrides[modality.toLowerCase()];
        if (configOverride) {
            effectiveConfig = extendMetaAnonymizationRule(
                effectiveConfig,
                configOverride
            );
        }
        return effectiveConfig;
    }
}
